import { getJob, getResourceInfo } from '@/api';
import { WS_EVENTS, WebSocketContext } from '@/providers/WebSocketProvider';
import { ExtendFileInfo } from '@/stores/cvc';
import { FETCH_STATUS, ResourceStatus } from '@/stores/resource';
import { getUrlExpiry } from '@/util/url';
import { useContext, useEffect, useRef } from 'react';
import { SetterOrUpdater } from 'recoil';

// i18n key shown on a result item whose job the server no longer knows about
// (expired, or discarded by an API restart) — resubmission is the only
// recovery. See ISSUE-022.
export const JOB_LOST_MESSAGE = 'Job no longer available. Please submit again.';
// Reconciliation issues GET /job in bounded batches, never one unbounded
// burst per in-flight job (ISSUE-022 AC6).
const RECONCILE_BATCH_SIZE = 5;
const TERMINAL_STATUSES: FETCH_STATUS[] = ['COMPLETE', 'FAILED'];

// ISSUE-076: getResourceInfo attempts, PER ROUTE into completeJob. The full
// rule, and why the two routes differ, is recorded next to getResourceInfo in
// src/api/index.ts — keep the two in sync.
//   live job_done — one attempt, terminal on the first failure (ISSUE-057).
//   reconciliation — a fixed second attempt before the same terminal ruling.
const LIVE_METADATA_ATTEMPTS = 1;
const RECONCILE_METADATA_ATTEMPTS = 2;
// Fixed, never exponential and never jittered: the batch's worst-case wait has
// to stay something that can be computed from constants (ISSUE-039's bound).
const RECONCILE_METADATA_RETRY_DELAY_MS = 250;

const isNotFoundError = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status === 404;

// Attempts are sequential within one job, so this adds nothing to how many
// requests reconciliation has in flight — RECONCILE_BATCH_SIZE still bounds
// that. The last attempt is the bare call, so the single-attempt (live) route
// keeps exactly the promise chain it had before ISSUE-076, not an equivalent
// one.
const fetchResourceInfo = (
  resourceId: string,
  attempts: number
): ReturnType<typeof getResourceInfo> => {
  if (attempts <= 1) return getResourceInfo(resourceId);
  return getResourceInfo(resourceId).catch(() =>
    new Promise<void>((resolve) => {
      setTimeout(resolve, RECONCILE_METADATA_RETRY_DELAY_MS);
    }).then(() => fetchResourceInfo(resourceId, attempts - 1))
  );
};

const useProcessingEvents = <E extends ExtendFileInfo>(
  setResultFileList: SetterOrUpdater<E[]>,
  processingStatus: ResourceStatus,
  setProcessingStatus: SetterOrUpdater<ResourceStatus>
) => {
  const { on, off, sessionId } = useContext(WebSocketContext);
  const processingStatusRef = useRef(processingStatus);
  processingStatusRef.current = processingStatus;

  useEffect(() => {
    if (!sessionId) return;
    const onPending = (data: any) => {
      if (!processingStatusRef.current[data.job_id]) return;
      setProcessingStatus((prev) => {
        const newStatus = { ...prev };
        newStatus[data.job_id] = 'PENDING';
        return newStatus;
      });
    };
    const onProcessing = (data: any) => {
      if (!processingStatusRef.current[data.job_id]) return;
      setProcessingStatus((prev) => {
        const newStatus = { ...prev };
        newStatus[data.job_id] = 'PROCESSING';
        return newStatus;
      });
    };
    const onFailed = (data: any) => {
      if (!processingStatusRef.current[data.job_id]) return;
      setProcessingStatus((prev) => {
        const newStatus = { ...prev };
        newStatus[data.job_id] = 'FAILED';
        return newStatus;
      });
    };
    // Deliberately does NOT clear isFetching, despite ISSUE-039's AC5 wording
    // ("the result item's isFetching is false — the row stops spinning").
    // That AC describes a mechanism this UI does not have. What actually stops
    // the spinner is fetchStatus turning FAILED: FileLoading swaps <Loading/>
    // for the failed treatment on `isFailed` (FileLoading.tsx:23-29), while
    // ResultFileListItem gates the WHOLE overlay on isFetching
    // (ResultFileListItem.tsx:117) — and that overlay is where the failure
    // text lives. So a false isFetching would delete the message AC6 requires,
    // not stop a spinner. It is invisible today only because every consumer
    // reads isFetching from a selector that recomputes it from the status map
    // (cvc.ts:418-420, mss.ts:92) and ignores the stored field; writing it
    // here is inert now and wrong the moment that changes. Pinned by
    // jobFailureSurface.test.tsx. (ISSUE-039 review, PR #458.)
    const markFailed = (jobId: string, failureMessage?: string) => {
      setProcessingStatus((prev) => {
        return { ...prev, [jobId]: 'FAILED' };
      });
      if (failureMessage) {
        setResultFileList((prev) => {
          return prev.map((item) => {
            return item.id === jobId ? { ...item, failureMessage } : item;
          });
        });
      }
    };
    // ISSUE-076: `attempts` is what distinguishes the two routes, and it is
    // passed at the CALL SITE — there are exactly two callers, the job_done
    // listener below and reconcileJob. Deliberately NOT a second parameter on
    // the listener itself: that would make the route depend on how many
    // arguments the dispatcher happens to pass, which is a property of
    // WebSocketProvider rather than of this file.
    const completeJob = (data: any, attempts: number) => {
      if (!processingStatusRef.current[data.job_id]) return;
      // Returned so the reconciliation path can await it; the chain never
      // rejects, so neither caller (the discarding WebSocket dispatch and
      // reconcileJob) can turn a failed metadata fetch into an unhandled
      // rejection or a job stuck in a non-terminal status (ISSUE-039).
      return fetchResourceInfo(data.resource_id, attempts)
        .then((res) => {
          const { data: resData } = res.data;
          setResultFileList((prev) => {
            return prev.map((item) => {
              if (item.id === data.job_id) {
                return {
                  ...item,
                  name: resData.name,
                  resource_id: data.resource_id,
                  originalUrl: resData.original.url,
                  transcodedUrl: resData.transcoded[0].url,
                  duration: resData.original.metadata.props.duration,
                  isFetching: false,
                  originalExpires: getUrlExpiry(resData.original.url),
                };
              }
              return item;
            });
          });
          setProcessingStatus((prev) => {
            const newStatus = { ...prev };
            newStatus[data.job_id] = 'COMPLETE';
            return newStatus;
          });
        })
        .catch((error) => {
          // console.error survives production builds (see ErrorBoundary.tsx).
          // No dedicated failureMessage: the row falls back to the default
          // 'Generating Failed' — JOB_LOST_MESSAGE stays reserved for a job
          // the server no longer knows about.
          console.error(
            'failed to load resource info',
            data.resource_id,
            error
          );
          markFailed(data.job_id);
        });
    };
    // The live job_done listener. It stays a single named const used for BOTH
    // `on` and `off` below: WebSocketProvider removes listeners by reference
    // identity (WebSocketProvider.tsx:62-69), so registering a wrapper here
    // and removing this name would leave the listener installed on every
    // cleanup. Pinned by the unmount case in useProcessingEvents.test.tsx.
    const onComplete = (data: any) => completeJob(data, LIVE_METADATA_ATTEMPTS);
    // --- ISSUE-022: reconnect reconciliation -------------------------------
    // A new session_id orphans in-flight jobs (their events stay bound to the
    // old session), so every non-terminal job is checked once against
    // GET /job/{job_id}. Triggered ONLY by SESSION_CHANGED — never polled.
    const reconcileJob = async (jobId: string) => {
      try {
        const { data } = await getJob(jobId);
        const job = data.data;
        if (job.status === 'done') {
          const resourceId = job.result?.resource_id;
          if (resourceId) {
            // Same path as a live job_done event, with one difference the
            // route is allowed: a bounded retry of the metadata read
            // (ISSUE-076). Awaited so the batch bound in onSessionChanged
            // covers the retry as well as the first request.
            await completeJob(
              { job_id: jobId, resource_id: resourceId },
              RECONCILE_METADATA_ATTEMPTS
            );
          } else {
            // Done without a result resource cannot be recovered client-side.
            markFailed(jobId, JOB_LOST_MESSAGE);
          }
        } else if (job.status === 'failed') {
          markFailed(jobId, JOB_LOST_MESSAGE);
        } else {
          // created / pending / processing — still in flight server-side;
          // reflect the reported state.
          setProcessingStatus((prev) => {
            return {
              ...prev,
              [jobId]: job.status === 'processing' ? 'PROCESSING' : 'PENDING',
            };
          });
        }
      } catch (error) {
        if (isNotFoundError(error)) {
          // Job expired (3-day Redis TTL) or discarded by a server restart —
          // terminal-unknown. Only resubmission can recover it.
          markFailed(jobId, JOB_LOST_MESSAGE);
        }
        // Transient errors (network, 5xx, timeout): leave the job untouched —
        // a later session change will reconcile it again.
      }
    };
    const onSessionChanged = async () => {
      const jobIds = Object.entries(processingStatusRef.current)
        .filter(([, status]) => !TERMINAL_STATUSES.includes(status))
        .map(([jobId]) => jobId);
      for (let i = 0; i < jobIds.length; i += RECONCILE_BATCH_SIZE) {
        const batch = jobIds.slice(i, i + RECONCILE_BATCH_SIZE);
        await Promise.all(batch.map(reconcileJob));
      }
    };
    // -----------------------------------------------------------------------

    on(WS_EVENTS.JOB_PENDING, onPending);
    on(WS_EVENTS.JOB_PROCESSING, onProcessing);
    on(WS_EVENTS.JOB_FAILED, onFailed);
    on(WS_EVENTS.JOB_DONE, onComplete);
    on(WS_EVENTS.SESSION_CHANGED, onSessionChanged);
    return () => {
      off(WS_EVENTS.JOB_PENDING, onPending);
      off(WS_EVENTS.JOB_PROCESSING, onProcessing);
      off(WS_EVENTS.JOB_FAILED, onFailed);
      off(WS_EVENTS.JOB_DONE, onComplete);
      off(WS_EVENTS.SESSION_CHANGED, onSessionChanged);
    };
  }, [sessionId, setProcessingStatus, setResultFileList, on, off]);
};
export default useProcessingEvents;
